import React from 'react';
import { FileText, FileCheck, Activity, TrendingUp, CheckCircle, Award } from 'lucide-react';
import { StatCard, Widget, StatusBadge } from './Common';

const capitalize = (str) => {
  if (!str) return '';
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

function HomeView({ overview, trends, reports, regularReports, topWeakCapabilities, topStrengths, onNavigate, onEventClick }) {
  if (!overview) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No data available yet. Create your first AAR to get started!</p>
      </div>
    );
  }

  const recentReports = (reports || []).slice(0, 5);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

      {/* Stat Cards */}
      <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total AARs"
          value={regularReports.length}
          icon={<FileText className="w-6 h-6" />}
          color="blue"
          subtitle="All reports"
        />
        <StatCard
          title="Completed"
          value={overview.completed_aars || 0}
          icon={<FileCheck className="w-6 h-6" />}
          color="green"
          subtitle="Ready to review"
        />
        <StatCard
          title="Processing"
          value={regularReports.filter(r => r.status === 'processing').length}
          icon={<Activity className="w-6 h-6" />}
          color="yellow"
          subtitle="In progress"
        />
        <StatCard
          title="Recent (30d)"
          value={overview.recent_activity_count || 0}
          icon={<TrendingUp className="w-6 h-6" />}
          color="purple"
          subtitle="Last month"
        />
      </div>

      {/* Capabilities of Focus */}
      <Widget title="Capabilities of Focus" subtitle="Top 5 areas needing attention" className="lg:col-span-1">
        <div className="space-y-3">
          {topWeakCapabilities && topWeakCapabilities.length > 0 ? (
            topWeakCapabilities.slice(0, 5).map((cap, index) => (
              <div key={index} className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="flex-shrink-0 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </span>
                    <span className="text-xs font-semibold text-red-900 uppercase">
                      {cap.nims_capability}
                    </span>
                  </div>
                  <span className="flex-shrink-0 px-2 py-1 bg-red-600 text-white text-xs font-bold rounded-full">
                    x{cap.frequency}
                  </span>
                </div>
                <p className="text-sm text-red-800 leading-tight">
                  {cap.gap_title || (cap.gap_description?.split(' ').slice(0, 8).join(' ') + '...')}
                </p>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No critical gaps identified</p>
            </div>
          )}
        </div>
      </Widget>

      {/* Operational Strengths */}
      <Widget
        title="Operational Strengths"
        subtitle="Top 5 recurring strengths"
        className="lg:col-span-1"
      >
        {topStrengths && topStrengths.length > 0 ? (
          <div className="space-y-3">
            {topStrengths.slice(0, 5).map((strength, index) => (
              <div
                key={index}
                className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg p-4 hover:shadow-md transition"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <h4 className="font-semibold text-green-900 uppercase text-xs tracking-wide">
                      {strength.category === 'General' ? 'Cross-Capability' : strength.category}
                    </h4>
                  </div>
                  <span className="px-2.5 py-0.5 bg-green-500 text-white rounded-full text-xs font-bold">
                    x{strength.count}
                  </span>
                </div>
                <p className="text-sm text-green-800 ml-10">
                  {strength.description?.length > 72
                    ? strength.description.slice(0, 72).trimEnd() + '...'
                    : strength.description}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Award className="w-16 h-16 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">No strengths identified yet</p>
            <p className="text-sm text-gray-500 mt-2">
              Complete AARs to identify operational strengths
            </p>
          </div>
        )}
      </Widget>

      {/* Recent AARs */}
      <Widget
        title="Recent AARs"
        action="View all..."
        onActionClick={() => onNavigate('reports')}
        className="lg:col-span-1"
      >
        <div className="space-y-3">
          {recentReports.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">No reports yet</p>
          ) : (
            recentReports.map(report => (
              <div
                key={report.aar_id}
                onClick={() => {
                  if (onEventClick) {
                    onEventClick(report.aar_id);
                  } else {
                    onNavigate('reports');
                  }
                }}
                className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 hover:shadow-sm transition cursor-pointer group"
              >
                <p className="font-medium text-sm text-gray-900 group-hover:text-blue-600 transition">
                  {report.incident_name}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {capitalize(report.incident_type)} • {capitalize(report.hazard_type)}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-400">
                    {new Date(report.incident_date).toLocaleDateString()}
                  </span>
                  <StatusBadge status={report.status} />
                </div>
              </div>
            ))
          )}
        </div>
      </Widget>

    </div>
  );
}

export default HomeView;